<script setup>
import { computed, inject, onMounted, ref } from 'vue'

const i18n = inject('i18n')
const t = (key, options) => i18n.t(key, options)

const props = defineProps({
  initialData: {
    type: Object,
    required: true
  }
})

const data = props.initialData

const palletFields = ['length_cm', 'width_cm', 'height_cm']

const newPallet = () => ({
  length_cm: data.defaultPalletSideCm,
  width_cm: data.defaultPalletSideCm,
  height_cm: ''
})

const dispatchedOn = ref(data.awb.dispatched_on || data.today)
const airportLocation = ref(data.awb.airport_location || '')
const awbNumber = ref(data.awb.awb_number || '')
const pickUpTruck = ref(data.awb.pick_up_truck || '')
const firstMileCarrier = ref(data.awb.first_mile_carrier || 'geps')
const pallets = ref(
  data.awb.pallets.length
    ? data.awb.pallets.map((pallet) =>
        Object.fromEntries(palletFields.map((field) => [field, pallet[field]]))
      )
    : [newPallet()]
)

const palletVolumetricKg = (pallet) =>
  palletFields.reduce((product, field) => product * (parseFloat(pallet[field]) || 0), 1) /
  data.volumetricWeightDivisor

const formatKg = (kg) => kg.toLocaleString(undefined, { maximumFractionDigits: 1 })

const { components, boxes } = data
const showBoxes = ref(false)

const atAirport = (component) =>
  !component.airport_location || component.airport_location === airportLocation.value
const removedComponentIds = ref(new Set())
const isRemoved = (component) => removedComponentIds.value.has(component.id)
const toggleComponent = (component) =>
  removedComponentIds.value.has(component.id)
    ? removedComponentIds.value.delete(component.id)
    : removedComponentIds.value.add(component.id)

const offeredComponents = computed(() =>
  components.filter((component) => !removedComponentIds.value.has(component.id))
)
const availableComponents = computed(() => offeredComponents.value.filter(atAirport))
const otherAirportCount = computed(
  () => offeredComponents.value.length - availableComponents.value.length
)

const availableBoxes = computed(() => {
  const componentIds = new Set(availableComponents.value.map((component) => component.id))
  return boxes.filter((box) => componentIds.has(box.shipment_component_id))
})

const includedBoxIds = ref(new Set(data.awb.shipment_box_ids))
const isIncluded = (box) => includedBoxIds.value.has(box.id)
const toggleBox = (box) =>
  isIncluded(box) ? includedBoxIds.value.delete(box.id) : includedBoxIds.value.add(box.id)
const includedBoxes = computed(() => availableBoxes.value.filter(isIncluded))

const removeTrackingText = ref('')
const unmatchedTrackingNumbers = ref([])
const removeByTrackingNumber = () => {
  const numbers = removeTrackingText.value.split(/\s+/).filter(Boolean)
  const matched = availableBoxes.value.filter((box) => numbers.includes(box.tracking_number))
  matched.forEach((box) => includedBoxIds.value.delete(box.id))

  const matchedNumbers = new Set(matched.map((box) => box.tracking_number))
  unmatchedTrackingNumbers.value = numbers.filter((number) => !matchedNumbers.has(number))
  removeTrackingText.value = unmatchedTrackingNumbers.value.join('\n')
}

const addShipmentNames = ref('')
const addShipments = (event) => {
  // form.submit() fires no submit event, so trigger Rails UJS manually
  const button = event.currentTarget
  $.rails.disableFormElement($(button))
  // Add the new shipments with a server-side reload for simplicity
  button.form.action = data.addShipmentsPath
  button.form.submit()
}

onMounted(() => $.onmount())
</script>

<template>
  <div>
    <input
      v-for="box in includedBoxes"
      :key="`included-${box.id}`"
      type="hidden"
      name="geps_awb[shipment_box_ids][]"
      :value="box.id"
    />
    <input
      v-for="component in offeredComponents"
      :key="`offered-${component.id}`"
      type="hidden"
      name="geps_awb[offered_component_ids][]"
      :value="component.id"
    />

    <div class="row">
      <div class="col-md-6 form-group">
        <label for="geps-awb-dispatched-on">{{ t('features.vue.geps_awbs.dispatched_on') }}</label>
        <input
          id="geps-awb-dispatched-on"
          v-model="dispatchedOn"
          type="date"
          name="geps_awb[dispatched_on]"
          class="form-control"
          required
        />
      </div>

      <div class="col-md-6 form-group">
        <label for="geps-awb-number">
          {{ t('features.vue.geps_awbs.awb_number') }}
          <span
            class="ri-information-fill text-muted ml-1"
            data-toggle="tooltip"
            :title="t('features.vue.geps_awbs.awb_number_hint')"
          ></span>
        </label>
        <input
          id="geps-awb-number"
          v-model="awbNumber"
          type="text"
          name="geps_awb[awb_number]"
          class="form-control"
          :placeholder="t('features.vue.geps_awbs.awb_number_placeholder')"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-md-4 form-group">
        <label for="geps-awb-airport">{{ t('features.vue.geps_awbs.airport_location') }}</label>
        <select
          id="geps-awb-airport"
          v-model="airportLocation"
          name="geps_awb[airport_location]"
          class="form-control"
          required
        >
          <option value="">{{ t('features.vue.geps_awbs.select_airport') }}</option>
          <option v-for="airport in data.airportLocations" :key="airport" :value="airport">
            {{ airport }}
          </option>
        </select>
      </div>

      <div class="col-md-4 form-group">
        <label for="geps-awb-pick-up-truck">
          {{ t('features.vue.geps_awbs.pick_up_truck') }}
          <span
            class="ri-information-fill text-muted ml-1"
            data-toggle="tooltip"
            :title="t('features.vue.geps_awbs.pick_up_truck_hint')"
          ></span>
        </label>
        <select
          id="geps-awb-pick-up-truck"
          v-model="pickUpTruck"
          name="geps_awb[pick_up_truck]"
          class="form-control"
        >
          <option value="">{{ t('features.vue.geps_awbs.select_pick_up_truck') }}</option>
          <option v-for="truck in data.pickUpTrucks" :key="truck" :value="truck">
            {{ t(`features.vue.geps_awbs.pick_up_trucks.${truck}`) }}
          </option>
        </select>
      </div>

      <div class="col-md-4 form-group">
        <label for="geps-awb-first-mile-carrier">
          {{ t('features.vue.geps_awbs.first_mile_carrier') }}
          <span
            class="ri-information-fill text-muted ml-1"
            data-toggle="tooltip"
            :title="t('features.vue.geps_awbs.first_mile_carrier_hint')"
          ></span>
        </label>
        <select
          id="geps-awb-first-mile-carrier"
          v-model="firstMileCarrier"
          name="geps_awb[first_mile_carrier]"
          class="form-control"
        >
          <option v-for="carrier in data.firstMileCarriers" :key="carrier" :value="carrier">
            {{ t(`features.vue.geps_awbs.first_mile_carriers.${carrier}`) }}
          </option>
        </select>
      </div>
    </div>

    <h5>{{ t('features.vue.geps_awbs.pallets') }}</h5>
    <div v-for="(pallet, index) in pallets" :key="`pallet-${index}`" class="card mb-2">
      <div class="card-body pb-0">
        <div class="d-flex align-items-center mb-2">
          <h6 class="mb-0">
            {{ t('features.vue.geps_awbs.pallet_title', { number: index + 1 }) }}
          </h6>
          <span v-if="data.volumetricWeightDivisor" class="text-muted ml-3">
            {{ t('features.vue.geps_awbs.volumetric_weight') }}:
            {{ t('features.vue.geps_awbs.kg', { kg: formatKg(palletVolumetricKg(pallet)) }) }}
          </span>
          <button
            v-if="pallets.length > 1"
            type="button"
            class="btn btn-sm btn-light ml-auto"
            @click="pallets.splice(index, 1)"
          >
            {{ t('features.vue.geps_awbs.remove') }}
          </button>
        </div>
        <div class="form-row">
          <div v-for="field in palletFields" :key="field" class="col-md-4 form-group">
            <label :for="`geps-awb-pallet-${index}-${field}`">
              {{ t(`features.vue.geps_awbs.${field}`) }}
            </label>
            <input
              :id="`geps-awb-pallet-${index}-${field}`"
              v-model="pallet[field]"
              type="number"
              min="1"
              class="form-control"
              :name="`geps_awb[pallets][][${field}]`"
              required
            />
          </div>
        </div>
      </div>
    </div>
    <p>
      <button type="button" class="btn btn-sm btn-light" @click="pallets.push(newPallet())">
        {{ t('features.vue.geps_awbs.add_pallet') }}
      </button>
      <span class="text-muted ml-2">
        {{ t('features.vue.geps_awbs.pallet_count', { count: pallets.length }) }}
      </span>
    </p>

    <hr />

    <h5>{{ t('features.vue.geps_awbs.shipments') }}</h5>
    <p v-if="otherAirportCount" class="text-muted">
      {{ t('features.vue.geps_awbs.other_airport_components', { count: otherAirportCount }) }}
    </p>

    <table class="table table-sm">
      <thead>
        <tr>
          <th>{{ t('features.vue.geps_awbs.shipment_name') }}</th>
          <th>{{ t('features.vue.geps_awbs.brand') }}</th>
          <th>{{ t('features.vue.geps_awbs.component_name') }}</th>
          <th>{{ t('features.vue.geps_awbs.shipping_method') }}</th>
          <th>{{ t('features.vue.geps_awbs.airport_location') }}</th>
          <th>{{ t('features.vue.geps_awbs.box_count_header') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="component in components"
          :key="`component-row-${component.id}`"
          :class="{ 'text-muted': isRemoved(component) || !atAirport(component) }"
        >
          <td>
            <a :href="`/shipments/${component.shipment_id}`" target="_blank">
              {{ component.shipment_name }}
            </a>
          </td>
          <td>{{ component.brand }}</td>
          <td>{{ component.component_name }}</td>
          <td>{{ component.shipping_method_name }}</td>
          <td>{{ component.airport_location }}</td>
          <td>{{ component.box_count }}</td>
          <td>
            <button
              v-if="atAirport(component)"
              type="button"
              class="btn btn-sm btn-light"
              @click="toggleComponent(component)"
            >
              {{
                isRemoved(component)
                  ? t('features.vue.geps_awbs.add_back')
                  : t('features.vue.geps_awbs.remove')
              }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="form-group">
      <label for="geps-awb-add-shipment-names">
        {{ t('features.vue.geps_awbs.add_shipments_label') }}
      </label>
      <textarea
        id="geps-awb-add-shipment-names"
        v-model="addShipmentNames"
        name="add_shipment_names"
        class="form-control"
        rows="2"
        :placeholder="t('features.vue.geps_awbs.whitespace_separated_placeholder')"
      ></textarea>
    </div>
    <p>
      <button
        type="button"
        class="btn btn-sm btn-light"
        :disabled="!addShipmentNames.trim()"
        :data-disable-with="t('features.vue.geps_awbs.adding_shipments')"
        @click="addShipments"
      >
        {{ t('features.vue.geps_awbs.add_shipments') }}
      </button>
    </p>

    <hr />

    <h5>{{ t('features.vue.geps_awbs.boxes') }}</h5>

    <div class="form-group">
      <label for="geps-awb-removed-tracking-numbers">
        {{ t('features.vue.geps_awbs.removed_tracking_numbers') }}
        <span
          class="ri-information-fill text-muted ml-1"
          data-toggle="tooltip"
          :title="t('features.vue.geps_awbs.removed_tracking_numbers_hint')"
        ></span>
      </label>
      <textarea
        id="geps-awb-removed-tracking-numbers"
        v-model="removeTrackingText"
        class="form-control"
        rows="4"
        :placeholder="t('features.vue.geps_awbs.whitespace_separated_placeholder')"
      ></textarea>
      <small v-if="unmatchedTrackingNumbers.length" class="form-text text-danger">
        {{
          t('features.vue.geps_awbs.unmatched_tracking_numbers', {
            numbers: unmatchedTrackingNumbers.join(', ')
          })
        }}
      </small>
    </div>
    <p>
      <button
        type="button"
        class="btn btn-sm btn-light"
        :disabled="!removeTrackingText.trim()"
        @click="removeByTrackingNumber"
      >
        {{ t('features.vue.geps_awbs.remove_boxes') }}
      </button>
    </p>

    <p>
      <button
        type="button"
        class="btn btn-outline-secondary border-0"
        :aria-expanded="showBoxes"
        @click="showBoxes = !showBoxes"
      >
        <span
          :class="['mr-1', showBoxes ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line']"
        ></span>
        {{
          t('features.vue.geps_awbs.box_list_toggle', {
            count: availableBoxes.length,
            removed: availableBoxes.filter(
              (box) => !box.cancelled_at && !box.other_awb_id && !isIncluded(box)
            ).length,
            cancelled: availableBoxes.filter((box) => box.cancelled_at && !box.other_awb_id).length,
            other_awb: availableBoxes.filter((box) => box.other_awb_id).length
          })
        }}
      </button>
    </p>

    <table v-if="showBoxes" class="table table-sm">
      <thead>
        <tr>
          <th></th>
          <th>{{ t('features.vue.geps_awbs.id') }}</th>
          <th>{{ t('features.vue.geps_awbs.box_name') }}</th>
          <th>{{ t('features.vue.geps_awbs.tracking_number') }}</th>
          <th>{{ t('features.vue.geps_awbs.weight_in_grams') }}</th>
          <th>{{ t('features.vue.geps_awbs.cancelled_at') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="box in availableBoxes"
          :key="`box-${box.id}`"
          :class="{ 'text-muted': !isIncluded(box) }"
        >
          <td>
            <input
              type="checkbox"
              :checked="isIncluded(box)"
              :disabled="box.other_awb_id"
              @change="toggleBox(box)"
            />
          </td>
          <td>
            <a :href="`/shipment_boxes/${box.id}`" target="_blank">{{ box.id }}</a>
          </td>
          <td>
            {{ box.box_name }}
            <a
              v-if="box.other_awb_id"
              :href="`/shipment_management/geps_awbs/${box.other_awb_id}`"
              target="_blank"
              class="small text-muted ml-1"
            >
              {{ t('features.vue.geps_awbs.on_other_awb', { awb: box.other_awb_name }) }}
            </a>
          </td>
          <td>{{ box.tracking_number }}</td>
          <td>{{ box.weight_in_grams }}</td>
          <td>{{ box.cancelled_at }}</td>
        </tr>
      </tbody>
    </table>

    <hr />

    <div class="row mb-3">
      <div v-if="data.volumetricWeightDivisor" class="col-6 col-md-3">
        <div class="text-muted small">
          {{ t('features.vue.geps_awbs.summary_volumetric_weight') }}
        </div>
        <div class="h5 mb-0">
          {{
            t('features.vue.geps_awbs.kg', {
              kg: formatKg(pallets.reduce((total, pallet) => total + palletVolumetricKg(pallet), 0))
            })
          }}
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="text-muted small">{{ t('features.vue.geps_awbs.summary_shipments') }}</div>
        <div class="h5 mb-0">
          {{ new Set(includedBoxes.map((box) => box.shipment_id)).size }}
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="text-muted small">{{ t('features.vue.geps_awbs.summary_boxes') }}</div>
        <div class="h5 mb-0">{{ includedBoxes.length }}</div>
      </div>
      <div class="col-6 col-md-3">
        <div class="text-muted small">{{ t('features.vue.geps_awbs.summary_box_weight') }}</div>
        <div class="h5 mb-0">
          {{
            t('features.vue.geps_awbs.kg', {
              kg: formatKg(
                includedBoxes.reduce((total, box) => total + (box.weight_in_grams || 0), 0) / 1000
              )
            })
          }}
        </div>
      </div>
    </div>

    <button
      type="submit"
      class="btn btn-primary"
      :disabled="!includedBoxes.length"
      :data-disable-with="t('features.vue.geps_awbs.submitting')"
    >
      {{ t('features.vue.geps_awbs.submit') }}
    </button>
  </div>
</template>
